import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { Question, QuestionResponse } from '../../../../interfaces/questions.interface';
import { CategoryOption, MatrixOption, MatrixQuestion } from '../../../../interfaces/matrix-question.interface';
import { CustomerQuestionnaireService } from '../../../../services/customer-questionnaire.service';
import { Subscription } from 'rxjs/internal/Subscription';
import { CstQuestionnaireService } from '../../../../services/cst-questionnaire.service';
import { CommonQuestionnaireService } from '../../../../services/common-questionnaire.service';
import { FormGroupExt } from '../../../../../../form-ext/form-group.ext';
import { FormBuilderExt } from '../../../../../../form-ext/form-builder.ext';
import { AbstractControl, UntypedFormControl, Validators } from '@angular/forms';
import { CifcReview } from '../../../../interfaces/cifc.interface';
import * as _ from 'lodash';

@Component({
    selector: 'app-matrix',
    templateUrl: './matrix.component.html',
    styleUrls: ['./matrix.component.scss'],
    standalone: false,
})
export class MatrixComponent implements OnChanges, OnInit, OnDestroy, AfterViewInit {
    @Input() question: Question;
    @Input() questionIndex: number;

    @ViewChild('titleTextarea') titleTextarea: ElementRef;
    public questionTitle: string;
    public questionPlaceholder: string;
    public descriptionPlaceholder = $localize`Description (optional)`;
    public optionColumnPlaceholder = $localize`Column option`;
    public optionRowPlaceholder = $localize`Row option`;
    public MIN_ROWS = 2;
    public MIN_COLUMNS = 2;
    public matrixGroup: FormGroupExt;
    public questionReview: CifcReview;
    public optionObj: MatrixOption = {
        id: '',
        label: '',
        selected_category: '',
    };

    public categoryObj: CategoryOption = {
        id: '',
        name: '',
    };

    public questionBody: MatrixQuestion;
    public currentAnswer: MatrixOption[];

    public subscription: Subscription;
    public questionSubscription: Subscription;
    public reviewSubscription: Subscription;
    public questionChangedOnReviewReset: Subscription;
    public showErrorSubscription: Subscription;

    constructor(
        public customerQuestionnaireService: CustomerQuestionnaireService,
        public cstQuestionnaireService: CstQuestionnaireService,
        public commonQuestionnaireService: CommonQuestionnaireService,
        public cdr: ChangeDetectorRef,
        public formBuilder: FormBuilderExt,
    ) {
        this.subscription = this.customerQuestionnaireService.onQuestionMarked().subscribe((question: Question) => {
            this.questionBody.options = this.currentAnswer;
        });
        if (this.commonQuestionnaireService.createMode) {
            this.questionSubscription = this.cstQuestionnaireService.getQuestionObs().subscribe((question) => {
                this.cstQuestionnaireService.buildQuestion(this, question);
            });
            this.showErrorSubscription = this.cstQuestionnaireService.showErrorOnForm.asObservable().subscribe((val) => {
                this.TitleControl?.markAsTouched();
                this.cdr.detectChanges();
            });
        }
    }

    public get TitleControl(): AbstractControl | null {
        return this.matrixGroup?.group.get('title');
    }

    public get DescriptionControl(): AbstractControl | null {
        return this.matrixGroup?.group.get('description');
    }

    ngOnInit() {
        this.initForm();
        this.reviewSubscription = this.commonQuestionnaireService.reviewAddedObs().subscribe((review: CifcReview) => {
            if (review?.question_id === this.question.id && review.company_id === this.commonQuestionnaireService.reviewCompanyId) {
                this.questionReview = review;
            }
        });
        if (this.commonQuestionnaireService.reviewMode) {
            this.questionChangedOnReviewReset = this.commonQuestionnaireService
                .questionChangedOnReviewResetObs()
                .subscribe((question: Question) => {
                    if (question.id === this.question.id && question.body.company_id === this.commonQuestionnaireService.reviewCompanyId) {
                        this.currentAnswer = question.body.options as MatrixOption[];
                        (this.questionReview as any) = _.cloneDeep(
                            this.commonQuestionnaireService.initialReviewQuestions.find((x) => x.id === this.question.id)?.cifc_review,
                        );
                        if (this.questionReview?.solved) {
                            (this.questionReview as any) = null;
                        }
                    }
                });
        }
    }

    ngAfterViewInit(): void {
        if (this.titleTextarea) {
            this.titleTextarea.nativeElement.style.height = this.titleTextarea.nativeElement.scrollHeight + 'px';
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes && changes.question && changes.question.currentValue !== changes.question.previousValue) {
            this.question = changes.question.currentValue;
            this.cstQuestionnaireService.buildQuestion(this, changes.question.currentValue);
            this.initForm();
            this.TitleControl?.setValue(this.question.title);
            this.DescriptionControl?.setValue(this.question.description);
        }
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
        this.questionSubscription?.unsubscribe();
        this.reviewSubscription?.unsubscribe();
        this.questionChangedOnReviewReset?.unsubscribe();
        this.showErrorSubscription?.unsubscribe();
        this.cdr.detach();
    }

    public onTitleChange() {
        this.question.title = this.TitleControl?.value;
    }

    public onDescriptionChange() {
        this.question.description = this.DescriptionControl?.value;
    }

    addCellsControls() {
        if (this.questionBody) {
            this.questionBody.categories.forEach((cat, index) => {
                if (this.matrixGroup && !this.matrixGroup?.group.contains('category_' + index)) {
                    const errorName = 'category_' + index;
                    this.matrixGroup?.group.addControl('category_' + index, new UntypedFormControl(cat.name || '', Validators.required));
                    this.matrixGroup?.addError(errorName, {
                        required: 'Text required',
                    });
                } else {
                    this.getOptionControl(index, 'category')?.setValue(cat.name);
                }
            });

            this.questionBody.options.forEach((opt, index) => {
                if (this.matrixGroup && !this.matrixGroup?.group.contains('option_' + index)) {
                    const errorName = 'option_' + index;
                    this.matrixGroup?.group.addControl('option_' + index, new UntypedFormControl(opt.label || '', Validators.required));
                    this.matrixGroup?.addError(errorName, {
                        required: 'Text required',
                    });
                } else {
                    this.getOptionControl(index, 'option')?.setValue(opt.label);
                }
            });
        }
    }

    public getOptionControl(index: number, type: 'category' | 'option') {
        return this.matrixGroup.group.get(type + '_' + index);
    }

    public allCellsCompleted() {
        let areAllCellsCompleted = true;
        this.questionBody?.categories.forEach((cat, i) => {
            if (
                this.matrixGroup.group.get('category_' + i) &&
                this.matrixGroup.group.get('category_' + i)?.touched &&
                this.matrixGroup.group.get('category_' + i)?.invalid
            ) {
                areAllCellsCompleted = false;
            }
        });
        this.questionBody?.options.forEach((opt, i) => {
            if (
                this.matrixGroup.group.get('option_' + i) &&
                this.matrixGroup.group.get('option_' + i)?.touched &&
                this.matrixGroup.group.get('option_' + i)?.invalid
            ) {
                areAllCellsCompleted = false;
            }
        });
        return areAllCellsCompleted;
    }

    public initForm() {
        this.matrixGroup = this.formBuilder.groupExt(
            {
                title: [this.question.title, [Validators.required]],
                description: [this.question.description],
            },
            {},
            {
                title: {
                    required: $localize`Please enter a title for this question`,
                },
            },
        );
        this.addCellsControls();
        this.cstQuestionnaireService.currentQuestionForm = this.matrixGroup;
    }

    public getCategoryStatus(option: MatrixOption, category: CategoryOption): boolean {
        return option.selected_category === category.id;
    }

    public onOptionChange(option: MatrixOption, category: CategoryOption) {
        this.question.answerChanged = true;
        option.selected_category = category.id;
    }

    public onQuestionMarked($event: boolean) {
        this.question.answerChanged = !$event;
    }

    public onCategoryChange(categoryIndex: number) {
        this.questionBody.categories[categoryIndex].name = this.matrixGroup.group.get('category_' + categoryIndex)?.value;
        this.cstQuestionnaireService.currentQuestionForm = this.matrixGroup;
    }

    public onOptionValueChanged(optionIndex: number) {
        this.questionBody.options[optionIndex].label = this.matrixGroup.group.get('option_' + optionIndex)?.value;
        this.cstQuestionnaireService.currentQuestionForm = this.matrixGroup;
    }

    public addCategory() {
        this.questionBody.categories.splice(
            this.questionBody.categories.length,
            0,
            this.cstQuestionnaireService.getObjectWithRandomId(this.categoryObj),
        );
        this.addCellsControls();
        this.cdr.detectChanges();
    }

    public shouldShowDeleteButtonForOption(optionIndex: number): boolean {
        return optionIndex <= this.questionBody.options.length;
    }

    public shouldShowDeleteButtonForCategory(columnIndex: number): boolean {
        return columnIndex <= this.questionBody.categories.length;
    }

    public shouldShowSelect(columnIndex: number, optionIndex: number): boolean {
        return columnIndex < this.questionBody.categories.length && optionIndex < this.questionBody.options.length;
    }

    public checkMinColRequirements() {
        return this.MIN_COLUMNS < this.questionBody.categories.length;
    }

    public checkMinRowRequirements() {
        return this.MIN_ROWS < this.questionBody.options.length;
    }

    public shouldShowEmptyBlock(columnIndex: number, optionIndex: number): boolean {
        return optionIndex === this.questionBody.options.length - 1 && columnIndex === this.questionBody.categories.length - 1;
    }

    public deleteOption(index: number, type: 'option' | 'column') {
        if (type === 'option') {
            if (index < this.questionBody.options.length) {
                return this.questionBody.options;
            }
        }

        if (type === 'column') {
            if (index < this.questionBody.categories.length) {
                return this.questionBody.categories;
            }
        }

        return [];
    }

    public deleteQuestionField(optionIndex: number, type: 'option' | 'column') {
        this.cstQuestionnaireService.deleteQuestionField(this.deleteOption(optionIndex, type), optionIndex, this.MIN_ROWS);
        this.matrixGroup.group.removeControl(type === 'option' ? 'option_' + optionIndex : 'category_' + optionIndex);
        this.addCellsControls();
        this.cstQuestionnaireService.currentQuestionForm = this.matrixGroup;
    }

    public getCategoryBasedOnIndex(index: number) {
        return this.questionBody.categories[index];
    }

    public getOptionBasedOnIndex(index: number) {
        return this.questionBody.options[index];
    }

    public hasAllOptionsChecked() {
        return (
            (this.questionBody.options.filter((x) => x.selected_category).length === this.questionBody.options.length &&
                this.question.answerChanged) ||
            (this.question.cifc_review && !this.question.cifc_review.solved) ||
            (this.question as unknown as QuestionResponse).body.deleted
        );
    }

    public addQuestionField(questionBody: any, object: any, customIndex?: number) {
        this.cstQuestionnaireService.addQuestionField(questionBody, object, customIndex);
        this.addCellsControls();

        this.cdr.detectChanges();
    }
}
